import { Component, WritableSignal, computed, inject, input, output, viewChild } from '@angular/core';
import { ColDef, ValueFormatterParams } from 'ag-grid-community';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { map, of, pipe, switchMap } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  Action,
  SelectedRowAction,
  SimpleGridComponent
} from '@app/shared/components/simple-grid/simple-grid.component';
import { DeleteEventDataModel } from '@app/shared/components/simple-grid/simple-grid.model';
import { RentalAgreementValuesUpsertDialogComponent } from './rental-agreement-values-upsert-dialog/rental-agreement-values-upsert-dialog.component';
import { ValueItemType } from '@app/core/models/common.model';
import { RentalAgreementValueDetailsModel, RentalAgreementValueListModel } from './rental-agreement-values.model';
import { RaVersion } from '../rental-agreements.model';

type RentalAgreementsValuesConfig =
  | {
      raMode: 'create';
      rentalAgreementUid: string;
      reservationNumber: string;
      rentalAgreementDays: number;
      rentalAgreementVersion: RaVersion;
      valueItems: WritableSignal<RentalAgreementValueDetailsModel[]>;
    }
  | {
      raMode: 'update';
      rentalAgreementUid: string;
      reservationNumber: string;
      rentalAgreementDays: number;
      rentalAgreementVersion: RaVersion;
      valueItems: WritableSignal<RentalAgreementValueDetailsModel[]>;
    }
  | {
      raMode: 'readonly';
      rentalAgreementUid: string;
      rentalAgreementVersion: RaVersion;
      valueItems: WritableSignal<RentalAgreementValueDetailsModel[]>;
    };

@Component({
  selector: 'app-rental-agreement-values',
  standalone: true,
  imports: [SimpleGridComponent],
  templateUrl: './rental-agreement-values.component.html',
  host: { class: 'd-flex flex-fill' }
})
export class RentalAgreementValuesComponent {
  private _modalService = inject(NgbModal);

  config = input.required<RentalAgreementsValuesConfig>();

  valuesChanged = output();

  private _grid = viewChild(SimpleGridComponent);
  private _items = computed(() => this.config().valueItems());

  refresh() {
    this._grid()?.refresh();
  }

  private isReadonly = computed(() => this.config().raMode === 'readonly');

  private reservationNumber = computed(() => {
    const config = this.config();
    return config.raMode === 'readonly' ? null : config.reservationNumber;
  });

  private rentalAgreementDays = computed(() => {
    const config = this.config();
    return config.raMode === 'readonly' ? null : config.rentalAgreementDays;
  });

  actions = computed<Action[]>(() => (this.isReadonly() ? [] : ['create']));

  deselectRowSignal$ = toObservable(this.config).pipe(map(() => undefined as void));

  listRentalAgreementValuesOperator = pipe(map(() => this._items()));

  createRentalAgreementValueOperator = pipe(
    switchMap(() => {
      const modalRef = this.openDialog();
      modalRef.componentInstance.config.set({
        mode: 'create',
        rentalAgreementUid: this.config().rentalAgreementUid,
        reservationNumber: this.reservationNumber()!,
        daysNumber: this.rentalAgreementDays()!
      });
      return modalRef.closed.pipe(
        map((value) => {
          this.config().valueItems.update((list) => [...list, value]);
          this.valuesChanged.emit();
        })
      );
    })
  );

  updateRentalAgreementValueOperator = pipe(
    switchMap((uid: string) => {
      const existing = this._items().find((x) => x.uid === uid);
      if (!existing) return of(undefined);
      const modalRef = this.openDialog();
      modalRef.componentInstance.config.set({
        mode: 'update',
        item: existing,
        reservationNumber: this.reservationNumber()!
      });
      return modalRef.closed.pipe(
        map((value) => {
          this.config().valueItems.update((list) => list.map((x) => (x.uid === uid ? value : x)));
          this.valuesChanged.emit();
        })
      );
    })
  );

  detailsRentalAgreementValueOperator = pipe(
    switchMap((uid: string) => {
      const existing = this._items().find((x) => x.uid === uid);
      if (!existing) return of(undefined);
      const modalRef = this.openDialog();
      modalRef.componentInstance.config.set({
        mode: 'readonly',
        item: existing,
        rentalAgreementVersion: this.config().rentalAgreementVersion
      });
      return modalRef.closed;
    })
  );

  deleteRentalAgreementValueOperator = pipe(
    switchMap((data: DeleteEventDataModel) => {
      this.config().valueItems.update((list) => list.filter((x) => x.uid !== data.uid));
      this.valuesChanged.emit();
      return of(undefined);
    })
  );

  selectedRowActionFilter = (row: unknown): SelectedRowAction[] => {
    if ((row as RentalAgreementValueListModel).isSoldByBroker || this.isReadonly()) {
      return ['details'];
    }
    return ['update', 'delete'];
  };

  private openDialog() {
    const options: NgbModalOptions = { size: 'lg', centered: true, scrollable: true, fullscreen: 'md' };
    return this._modalService.open(RentalAgreementValuesUpsertDialogComponent, options);
  }

  columnDefs: ColDef[] = [
    {
      headerName: 'Item Type',
      field: 'itemType',
      valueFormatter: (params: ValueFormatterParams) => {
        return ValueItemType[params.value];
      },
      filter: false,
      floatingFilter: false,
      sort: 'asc',
      sortIndex: 1,
      sortable: false
    },
    {
      headerName: 'Code',
      field: 'code',
      valueFormatter: (params: ValueFormatterParams) => {
        return params.value.toUpperCase();
      },
      filter: false,
      floatingFilter: false,
      sortable: false
    },
    {
      field: 'amountWithoutTax',
      valueFormatter: (params: ValueFormatterParams) => {
        return (Math.round((params.value + Number.EPSILON) * 100) / 100).toString();
      },
      filter: false,
      floatingFilter: false,
      sortable: false
    },
    {
      field: 'isSoldByBroker',
      headerName: 'Is Invoiced',
      filter: false,
      floatingFilter: false,
      sortable: false
    },
    {
      sort: 'desc',
      sortIndex: 2,
      field: 'isIncluded',
      filter: false,
      floatingFilter: false,
      sortable: false
    }
  ];
}
